import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  computed,
  contentChildren,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import type { NzTableFilterFn, NzTableQueryParams, NzTableSortFn } from 'ng-zorro-antd/table';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropdownMenuComponent, NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

import type { GridConfig } from '../../core/interfaces/grid-config.types';
import { DEFAULT_GRID_CONFIG } from '../../core/interfaces/grid-config.types';
import { CompanyCellDefDirective } from '../../directives/cell-def.directive';
import type { CompanyRowAction } from '../../types/company-row-action.types';
import type {
  CompanyTableColumnDef,
  CompanyTableColumnFilterOption,
  CompanyTableFilterChange,
  CompanyTableViewRow,
} from '../../types/company-table-column.types';
import type { CompanyTablePaginationMode } from '../../types/company-table-pagination.types';
import { formatTableCell } from '../../utils/grid-cell-format';
import { resolveTableColumns } from '../../utils/grid-columns-resolve';
import { resolveEllipsisTooltip } from '../../utils/grid-ellipsis-tooltip';
import {
  buildAutoFilterOptions,
  defaultColumnFilterMatch,
} from '../../utils/grid-filter-auto';
import { buildTableViewRows } from '../../utils/grid-view-rows';
import type { GridCellContext } from '../../core/interfaces/grid-context.types';

const DEFAULT_COLUMN_WIDTH_PX = 160;
const ACTIONS_COLUMN_WIDTH_PX = 120;

function normalizeNzFilterSelected(selected: unknown): unknown[] {
  if (selected == null) return [];
  return Array.isArray(selected) ? selected : [selected];
}

function parseColumnWidthPx(width?: number): number | null {
  return width != null && width > 0 ? width : null;
}

export interface CompanyTableDisplayColumn extends CompanyTableColumnDef {
  isSearchFilter: boolean;
  searchPlaceholder: string;
  nzFilters: Array<{ text: string; value: unknown; byDefault?: boolean }>;
  nzFilterFn?: NzTableFilterFn<CompanyTableViewRow> | boolean;
  nzSortFn?: NzTableSortFn<CompanyTableViewRow> | false;
}

/**
 * Tabela produtiva do dia a dia — API declarativa com `[columns]` + `[data]`.
 *
 * Para casos avançados (plugins, undo/redo, templates por coluna no HTML),
 * use `company-nztable-data-grid`.
 */
@Component({
  selector: 'company-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    NgTemplateOutlet,
    NzButtonModule,
    NzDropDownModule,
    NzIconModule,
    NzInputModule,
    NzMenuModule,
    NzTableModule,
    NzTagModule,
    NzTooltipModule,
  ],
  templateUrl: './company-table.component.html',
  styleUrl: './company-table.component.scss',
})
export class CompanyTableComponent implements OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly cellDefs = contentChildren(CompanyCellDefDirective);

  // ── Dados ─────────────────────────────────────────────────────────────────
  readonly columns = input<readonly CompanyTableColumnDef[]>([]);
  readonly data = input<readonly Record<string, unknown>[]>([]);
  readonly loading = input(false);
  readonly config = input<Partial<GridConfig>>({});

  // ── Paginação ─────────────────────────────────────────────────────────────
  readonly pagination = input<CompanyTablePaginationMode>('none');
  readonly pageIndex = input(1);
  readonly pageSize = input(DEFAULT_GRID_CONFIG.pageSize);
  readonly total = input(0);
  readonly showPageSizeChanger = input(true);
  readonly pageSizeOptions = input<readonly number[]>([10, 25, 50]);

  // ── Seleção ─────────────────────────────────────────────────────────────
  readonly showCheckbox = input(false);
  /**
   * Clique na linha alterna seleção.
   * Padrão: `true` quando `showCheckbox` está ativo; `false` caso contrário.
   * Use `[rowClickSelects]="false"` para desabilitar.
   */
  readonly rowClickSelects = input<boolean | undefined>(undefined);

  // ── Colunas ───────────────────────────────────────────────────────────────
  /** Habilita redimensionamento manual de colunas via arraste no header. */
  readonly columnResizable = input(true);

  // ── Ações de linha ────────────────────────────────────────────────────────
  readonly rowActions = input<readonly CompanyRowAction[]>([]);
  readonly actionsColumnLabel = input('Ações');
  readonly actionsColumnWidth = input(120);

  // ── Empty state ───────────────────────────────────────────────────────────
  readonly emptyTitle = input('Nenhum dado');
  readonly emptyDescription = input('Não há registros para exibir.');

  // ── Formatação ────────────────────────────────────────────────────────────
  readonly numberFractionDigits = input(2);
  readonly scrollX = input<string | undefined>(undefined);

  // ── Outputs ───────────────────────────────────────────────────────────────
  readonly pageIndexChange = output<number>();
  readonly pageSizeChange = output<number>();
  readonly filterChange = output<readonly CompanyTableFilterChange[]>();
  readonly selectionChange = output<readonly Record<string, unknown>[]>();
  readonly rowClick = output<{ row: Record<string, unknown> }>();
  readonly columnWidthChange = output<{ field: string; width: number }>();
  readonly actionClick = output<{ actionId: string; row: Record<string, unknown> }>();

  private readonly columnWidthOverrides = signal<Record<string, number>>({});
  protected readonly isResizingColumn = signal(false);
  protected readonly resizingColumnField = signal<string | null>(null);
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private resizingField: string | null = null;
  private resizingColumnDef: CompanyTableColumnDef | null = null;

  private readonly activeColumnFilters = signal<Record<string, unknown[]>>({});
  private readonly activeListFilters = signal<Record<string, unknown[]>>({});
  private readonly searchFilterDrafts = signal<Record<string, string>>({});
  private readonly searchFilterActive = signal<Record<string, string>>({});
  protected readonly searchPanelVisible = signal<Record<string, boolean>>({});
  protected readonly activeSearchField = signal<string | null>(null);
  protected readonly searchDropdownMenu = viewChild<NzDropdownMenuComponent>('searchDropdownMenu');
  private readonly currentPageViewRows = signal<readonly CompanyTableViewRow[]>([]);
  protected readonly viewRows = signal<CompanyTableViewRow[]>([]);

  protected allChecked = false;
  protected indeterminate = false;

  protected readonly mergedConfig = computed(() => ({ ...DEFAULT_GRID_CONFIG, ...this.config() }));

  protected readonly filteredRows = computed(() => {
    const rows = this.data();
    const activeFilters = this.activeColumnFilters();
    const searchFilters = this.searchFilterActive();
    if (Object.keys(activeFilters).length === 0 && Object.keys(searchFilters).length === 0) {
      return rows;
    }
    return rows.filter(row => this.matchesActiveFilters(row, activeFilters, searchFilters));
  });

  protected readonly rowsForView = computed(() =>
    this.pagination() === 'server' ? this.data() : this.filteredRows(),
  );

  protected readonly resolvedColumns = computed(() =>
    resolveTableColumns(this.columns(), [...this.filteredRows()]),
  );

  protected readonly displayColumns = computed((): CompanyTableDisplayColumn[] => {
    const rows = this.data();
    const digits = this.numberFractionDigits();
    const activeListFilters = this.activeListFilters();

    return this.resolvedColumns().map(col => {
      const f = col.filter;
      const field = col.field;
      const isSearchFilter = typeof f === 'object' && f !== null && f.mode === 'search';
      const searchPlaceholder =
        typeof f === 'object' && f !== null && f.placeholder ? f.placeholder : 'Buscar...';
      const isServerPagination = this.pagination() === 'server';

      const manualOptions =
        typeof f === 'object' && f !== null && f.options && f.options.length > 0 ? f.options : null;

      const filterOff =
        f === undefined ||
        f === false ||
        isSearchFilter ||
        (typeof f === 'object' &&
          f !== null &&
          f.auto === false &&
          !(f.options && f.options.length > 0));

      const useAuto =
        !filterOff && !manualOptions && (f === true || (typeof f === 'object' && f !== null));

      let options: CompanyTableColumnFilterOption[] = [];
      if (manualOptions) {
        options = [...manualOptions];
      } else if (useAuto) {
        options = buildAutoFilterOptions(
          this.rowsForColumnOptions(rows, this.activeColumnFilters(), field),
          col,
          digits,
        );
      }

      const nzFilters = options.map(o => ({
        text: o.text,
        value: o.value,
        byDefault: isServerPagination
          ? (activeListFilters[field] ?? []).some(
              selected => selected === o.value || defaultColumnFilterMatch(selected, o.value),
            )
          : (o.byDefault ?? false),
      }));

      const custom = typeof f === 'object' && f !== null ? f.filterFn : undefined;
      const nzFilterFn: NzTableFilterFn<CompanyTableViewRow> | boolean | undefined =
        isSearchFilter
          ? undefined
          : nzFilters.length > 0
            ? isServerPagination
              ? true
              : (selected, row) => {
                  const rawSel = normalizeNzFilterSelected(selected);
                  if (rawSel.length === 0) return true;
                  const cell = row.record[field];
                  if (custom) return custom(rawSel, cell, row.record);
                  return rawSel.some(sel => defaultColumnFilterMatch(sel, cell));
                }
            : undefined;

      const nzSortFn: NzTableSortFn<CompanyTableViewRow> | false = col.sortable
        ? (a, b) => {
            const va = a.record[field];
            const vb = b.record[field];
            if (typeof va === 'number' && typeof vb === 'number') return va - vb;
            return String(va ?? '').localeCompare(String(vb ?? ''), 'pt-BR');
          }
        : false;

      return { ...col, isSearchFilter, searchPlaceholder, nzFilters, nzFilterFn, nzSortFn };
    });
  });

  protected readonly nzFrontPagination = computed(() => this.pagination() === 'client');
  protected readonly nzShowPagination = computed(
    () => this.pagination() === 'client' || this.pagination() === 'server',
  );

  protected readonly nzScroll = computed<{ x?: string; y?: string }>(() => {
    const cfg = this.mergedConfig();
    const explicit = this.scrollX();
    const scroll: { x?: string; y?: string } = {};

    if (explicit) {
      scroll.x = explicit;
    } else {
      const cols = this.displayColumns();
      if (cols.length > 0) {
        let total = this.showCheckbox() ? 48 : 0;
        for (const col of cols) {
          total += this.effectiveColumnWidthPx(col);
        }
        if (this.hasRowActions()) {
          total += this.actionsColumnWidth() || ACTIONS_COLUMN_WIDTH_PX;
        }
        scroll.x = `${total}px`;
      }
    }

    if (cfg.scrollY) scroll.y = cfg.scrollY;
    return scroll;
  });

  protected readonly hasRowActions = computed(() => this.rowActions().length > 0);

  protected readonly hasSearchFilterColumns = computed(() =>
    this.displayColumns().some(col => col.isSearchFilter),
  );

  protected readonly isRowClickSelectionEnabled = computed(() => {
    const configured = this.rowClickSelects();
    if (configured !== undefined) return configured;
    return this.showCheckbox();
  });

  protected readonly activeSearchPlaceholder = computed(() => {
    const field = this.activeSearchField();
    if (!field) return 'Buscar...';
    return this.displayColumns().find(col => col.field === field)?.searchPlaceholder ?? 'Buscar...';
  });

  protected readonly tableLayout = computed<'auto' | 'fixed'>(() =>
    this.mergedConfig().ellipsis ? 'fixed' : 'auto',
  );

  constructor() {
    effect(() => {
      const data = this.rowsForView();
      untracked(() => this.viewRows.set(buildTableViewRows(data)));
    });
  }

  ngOnDestroy(): void {
    this.detachColumnResizeListeners();
  }

  protected tableSize(): 'default' | 'middle' | 'small' {
    return this.mergedConfig().size;
  }

  protected isBordered(): boolean {
    return this.mergedConfig().bordered;
  }

  protected isEllipsis(): boolean {
    return this.mergedConfig().ellipsis;
  }

  protected paginationType(): 'default' | 'small' {
    return this.mergedConfig().paginationType;
  }

  protected formatCellValue(value: unknown, col: CompanyTableColumnDef): string {
    return formatTableCell(value, col, { numberFractionDigits: this.numberFractionDigits() });
  }

  /** Tooltip exibido apenas quando o texto da célula está truncado por ellipsis. */
  private readonly _ellipsisTooltip = signal<string | null>(null);
  protected readonly ellipsisTooltip = this._ellipsisTooltip.asReadonly();

  protected onEllipsisTextEnter(event: MouseEvent, text: string): void {
    const target = event.currentTarget;
    if (!(target instanceof HTMLElement)) {
      this._ellipsisTooltip.set(null);
      return;
    }
    this._ellipsisTooltip.set(resolveEllipsisTooltip(target, text));
  }

  protected cellTemplateFor(field: string) {
    return this.cellDefs().find(def => def.field() === field)?.template ?? null;
  }

  protected cellContext(viewRow: CompanyTableViewRow, col: CompanyTableColumnDef): GridCellContext {
    const value = viewRow.record[col.field];
    return { value, row: viewRow.record, rowIndex: -1, column: { field: col.field, headerName: col.headerName } };
  }

  protected columnWidth(col: CompanyTableColumnDef): string | null {
    return `${this.effectiveColumnWidthPx(col)}px`;
  }

  protected effectiveColumnWidthPx(col: CompanyTableColumnDef): number {
    const override = this.columnWidthOverrides()[col.field];
    if (override != null && override > 0) return override;
    return parseColumnWidthPx(col.width) ?? DEFAULT_COLUMN_WIDTH_PX;
  }

  protected isColumnResizable(col: CompanyTableColumnDef): boolean {
    if (!this.columnResizable()) return false;
    if (col.resizable === false) return false;
    return true;
  }

  protected onColumnResizePointerDown(event: PointerEvent, col: CompanyTableColumnDef): void {
    if (event.button !== 0 || !this.isColumnResizable(col)) return;
    event.preventDefault();
    event.stopPropagation();

    this.resizeStartX = event.clientX;
    this.resizeStartWidth = this.effectiveColumnWidthPx(col);
    this.resizingField = col.field;
    this.resizingColumnDef = col;
    this.isResizingColumn.set(true);
    this.resizingColumnField.set(col.field);

    document.addEventListener('pointermove', this.onColumnResizeMove);
    document.addEventListener('pointerup', this.onColumnResizeEnd);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }

  private readonly onColumnResizeMove = (event: PointerEvent): void => {
    if (!this.resizingField || !this.resizingColumnDef) return;
    const delta = event.clientX - this.resizeStartX;
    const nextWidth = this.clampColumnWidth(this.resizingColumnDef, this.resizeStartWidth + delta);
    this.columnWidthOverrides.update(current => ({ ...current, [this.resizingField!]: nextWidth }));
  };

  private readonly onColumnResizeEnd = (): void => {
    const field = this.resizingField;
    const width = field ? this.columnWidthOverrides()[field] : undefined;
    this.detachColumnResizeListeners();
    if (field && width != null && width > 0) {
      this.columnWidthChange.emit({ field, width });
    }
  };

  private detachColumnResizeListeners(): void {
    this.isResizingColumn.set(false);
    this.resizingColumnField.set(null);
    this.resizingField = null;
    this.resizingColumnDef = null;
    document.removeEventListener('pointermove', this.onColumnResizeMove);
    document.removeEventListener('pointerup', this.onColumnResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }

  private clampColumnWidth(col: CompanyTableColumnDef, width: number): number {
    const min = col.minWidth ?? 60;
    const max = col.maxWidth ?? 640;
    return Math.min(max, Math.max(min, Math.round(width)));
  }

  protected preventHeaderSort(event: MouseEvent): void {
    event.stopPropagation();
  }

  protected filterMultipleEnabled(col: CompanyTableColumnDef): boolean {
    const f = col.filter;
    if (typeof f === 'object' && f !== null && f.multiple === false) return false;
    return true;
  }

  protected statusLabel(value: unknown, col: CompanyTableColumnDef): string {
    const labels = col.rendererParams?.['labels'] as Record<string, string> | undefined;
    const key = String(value ?? '');
    return labels?.[key] ?? (key || '—');
  }

  protected statusTagColor(value: unknown, col: CompanyTableColumnDef): string {
    const colors = col.rendererParams?.['colors'] as Record<string, string> | undefined;
    const key = String(value ?? '');
    return colors?.[key] ?? 'default';
  }

  protected statusShowsSpinner(value: unknown, col: CompanyTableColumnDef): boolean {
    const spinValues = col.rendererParams?.['spinValues'] as string[] | undefined;
    return spinValues?.includes(String(value ?? '')) ?? false;
  }

  protected onTableQueryParamsChange(params: NzTableQueryParams): void {
    if (this.pagination() === 'server') {
      // Filtros de lista no modo server são tratados em onListFilterChange (nzFilterChange).
      // Ignorar params.filter aqui evita reset após reload de dados.
      return;
    }

    const nextFilters: Record<string, unknown[]> = {};
    for (const filter of params.filter) {
      const key = filter.key;
      const selected = normalizeNzFilterSelected(filter.value);
      if (selected.length > 0) nextFilters[key] = selected;
    }

    this.activeColumnFilters.set(nextFilters);
  }

  protected onListFilterChange(field: string, value: unknown): void {
    if (this.pagination() !== 'server') return;

    const selected = normalizeNzFilterSelected(value);
    this.activeListFilters.update(current => {
      const next = { ...current };
      if (selected.length > 0) next[field] = selected;
      else delete next[field];
      return next;
    });
    this.emitServerFilters();
  }

  protected isSearchPanelVisible(field: string): boolean {
    return this.searchPanelVisible()[field] ?? false;
  }

  protected setSearchPanelVisible(field: string, visible: boolean): void {
    this.searchPanelVisible.update(current => ({ ...current, [field]: visible }));
  }

  protected onSearchPanelVisibleChange(field: string, visible: boolean): void {
    if (visible) {
      this.activeSearchField.set(field);
      const activeValue = this.searchFilterActive()[field];
      if (activeValue && !this.searchFilterDrafts()[field]) {
        this.searchFilterDrafts.update(current => ({ ...current, [field]: activeValue }));
      }
    }
    this.setSearchPanelVisible(field, visible);
  }

  protected applyActiveSearchFilter(): void {
    const field = this.activeSearchField();
    if (!field) return;
    this.applySearchFilter(field);
  }

  protected resetActiveSearchFilter(): void {
    const field = this.activeSearchField();
    if (!field) return;
    this.resetSearchFilter(field);
  }

  protected searchDraft(field: string): string {
    return this.searchFilterDrafts()[field] ?? '';
  }

  protected onSearchDraftChange(field: string, value: string): void {
    this.searchFilterDrafts.update(current => ({ ...current, [field]: value }));
  }

  protected isSearchFilterActive(field: string): boolean {
    return !!(this.searchFilterActive()[field]);
  }

  protected applySearchFilter(field: string): void {
    const value = (this.searchFilterDrafts()[field] ?? '').trim();
    this.searchFilterActive.update(current => ({ ...current, [field]: value }));
    this.setSearchPanelVisible(field, false);

    if (this.pagination() === 'server') {
      this.emitServerFilters();
      return;
    }

    this.activeColumnFilters.update(current => {
      const next = { ...current };
      if (value) next[field] = [value];
      else delete next[field];
      return next;
    });
  }

  protected resetSearchFilter(field: string): void {
    this.searchFilterDrafts.update(current => ({ ...current, [field]: '' }));
    this.searchFilterActive.update(current => ({ ...current, [field]: '' }));
    this.setSearchPanelVisible(field, false);

    if (this.pagination() === 'server') {
      this.emitServerFilters();
      return;
    }

    this.activeColumnFilters.update(current => {
      const next = { ...current };
      delete next[field];
      return next;
    });
  }

  protected onPageIndexChange(page: number): void {
    this.pageIndexChange.emit(page);
  }

  protected onPageSizeChange(size: number): void {
    this.pageSizeChange.emit(size);
  }

  protected onCurrentPageDataChange(rows: readonly CompanyTableViewRow[]): void {
    this.currentPageViewRows.set(rows);
    this.refreshHeaderCheckboxState(rows);
  }

  protected onAllChecked(checked: boolean): void {
    const pageData = this.currentPageViewRows();
    for (const row of pageData) {
      if (!row.disabled) row.checked = checked;
    }
    this.refreshHeaderCheckboxState(pageData);
    this.emitSelection();
  }

  protected onRowCheckedChange(): void {
    const pageData = this.currentPageViewRows();
    this.refreshHeaderCheckboxState(pageData);
    this.emitSelection();
  }

  protected isActionDisabled(action: CompanyRowAction, viewRow: CompanyTableViewRow): boolean {
    return action.disabledPredicate?.(viewRow.record) ?? false;
  }

  protected onRowActionClick(action: CompanyRowAction, viewRow: CompanyTableViewRow, event: Event): void {
    event.stopPropagation();
    if (this.isActionDisabled(action, viewRow)) return;
    this.actionClick.emit({ actionId: action.id, row: viewRow.record });
  }

  protected onSubmenuItemClick(
    item: { id: string },
    viewRow: CompanyTableViewRow,
    event: Event,
  ): void {
    event.stopPropagation();
    this.actionClick.emit({ actionId: item.id, row: viewRow.record });
  }

  protected onRowClick(viewRow: CompanyTableViewRow, event: MouseEvent): void {
    if (!this.isRowClickSelectionEnabled()) return;
    if (this.isInteractiveClickTarget(event.target)) return;

    if (this.showCheckbox() && !viewRow.disabled) {
      viewRow.checked = !viewRow.checked;
      this.refreshHeaderCheckboxState(this.currentPageViewRows());
      this.emitSelection();
    }

    this.rowClick.emit({ row: viewRow.record });
  }

  private isInteractiveClickTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    return !!target.closest(
      'button, a, input, textarea, select, label, .ant-checkbox, .ant-dropdown, .company-table__action-btn',
    );
  }

  private refreshHeaderCheckboxState(pageRows: readonly CompanyTableViewRow[]): void {
    const enabled = pageRows.filter(r => !r.disabled);
    const all = enabled.length > 0 && enabled.every(r => r.checked);
    const none = enabled.every(r => !r.checked);
    this.allChecked = all;
    this.indeterminate = !all && !none;
    this.cdr.markForCheck();
  }

  private emitSelection(): void {
    const selected = this.viewRows().filter(r => r.checked).map(r => r.record);
    this.selectionChange.emit(selected);
  }

  private emitServerFilters(): void {
    const changes: CompanyTableFilterChange[] = [];

    for (const [field, values] of Object.entries(this.activeListFilters())) {
      changes.push({ field, values });
    }
    for (const [field, term] of Object.entries(this.searchFilterActive())) {
      if (term) changes.push({ field, values: [term] });
    }

    this.filterChange.emit(changes);
  }

  private rowsForColumnOptions(
    rows: readonly Record<string, unknown>[],
    activeFilters: Record<string, unknown[]>,
    targetField: string,
  ): readonly Record<string, unknown>[] {
    const searchFilters = this.searchFilterActive();
    return rows.filter(row => {
      const filtersWithoutTarget = { ...activeFilters };
      delete filtersWithoutTarget[targetField];
      const searchWithoutTarget = { ...searchFilters };
      delete searchWithoutTarget[targetField];
      return this.matchesActiveFilters(row, filtersWithoutTarget, searchWithoutTarget);
    });
  }

  private matchesActiveFilters(
    row: Record<string, unknown>,
    activeFilters: Record<string, unknown[]>,
    searchFilters: Record<string, string> = {},
  ): boolean {
    for (const [field, term] of Object.entries(searchFilters)) {
      if (!term) continue;
      const cell = String(row[field] ?? '');
      if (!cell.toLowerCase().includes(term.toLowerCase())) return false;
    }

    for (const [filterKey, selected] of Object.entries(activeFilters)) {
      if (selected.length === 0) continue;
      const col = this.resolvedColumns().find(c => c.field === filterKey);
      if (!col) continue;
      const filterCfg = typeof col.filter === 'object' && col.filter !== null ? col.filter : null;
      const custom = filterCfg?.filterFn;
      const cell = row[filterKey];
      const matched = custom
        ? custom(selected, cell, row)
        : selected.some(sel => defaultColumnFilterMatch(sel, cell));
      if (!matched) return false;
    }
    return true;
  }
}
